import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, isValid } from 'date-fns';
import { getAllTags } from '../../../../../services/tagsApi'; // Adjusted path
import { showToast } from '../../../../../utils/toast/toast'; // Adjusted path
import { activeStatus } from '../../../../../utils/envConfig'; // Adjusted path

// URL Query Parameter Names
const URL_PARAM_STATUS = 'status';
const URL_PARAM_ROLE = 'role';
const URL_PARAM_LEAD_SOURCES = 'leadSources';
const URL_PARAM_TAGS = 'tags';
const URL_PARAM_CHANNEL_PARTNERS = 'partner'; // Changed from 'channelPartners'
const URL_PARAM_START_DATE = 'startDate';
const URL_PARAM_END_DATE = 'endDate';
const URL_PARAM_AGENT_ID = 'agentId';
const URL_PARAM_NEW_SORT = 'newSort';
const URL_PARAM_SEARCH_QUERY = 'q';
const URL_PARAM_LEAD_STATUS = 'leadStatus';
const URL_PARAM_LEAD_LABEL = 'leadLabel';
const URL_PARAM_PAGE = 'page';

const useLeadsListFilters = (axios, initialRole, setCurrentPage, initialOptAgents = [], initialOptLeadSource = [], viewMode = "default") => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Filter States
    const [filterStatus, setFilterStatus] = useState(null);
    const [filterRole, setFilterRole] = useState(initialRole || null);
    const [selectedOptions, setSelectedOptions] = useState(viewMode === "channelPartner" ? [] : []); // For lead source RSelect
    const [filterLeadSource, setFilterLeadSource] = useState(viewMode === "channelPartner" ? null : null); // Array of source names for API
    const [selectedFilterTags, setSelectedFilterTags] = useState(viewMode === "channelPartner" ? [] : []);
    const [selectedChannelPartners, setSelectedChannelPartners] = useState([]);
    const [rangeStart, setRangeStart] = useState(null);
    const [rangeEnd, setRangeEnd] = useState(null);
    const [filterAgent, setFilterAgent] = useState(null); // Agent ID
    const [selectedAgent, setSelectedAgent] = useState({ value: 'all', label: 'All Agents' }); // Agent object for RSelect
    const [newLeadsSort, setNewLeadsSort] = useState(false);
    const [search, setSearch] = useState(null); // Search input value
    const [filterSearch, setFilterSearch] = useState(null); // Debounced/submitted search term for API
    const [filterLeadStatus, setFilterLeadStatus] = useState(null);
    const [filterLeadLabel, setFilterLeadLabel] = useState(viewMode === "channelPartner" ? null : null);
    const [temporaryLabel, setTemporaryLabel] = useState(viewMode === "channelPartner" ? null : null); // For display in LeadLabel dropdown
    const [isFiltering, setIsFiltering] = useState(false);

    // Memoized data stores
    const [memoizedColdLeads, setMemoizedColdLeads] = useState(null);
    const [memoizedHotLeads, setMemoizedHotLeads] = useState(null);

    // Data for filters
    const [allTags, setAllTags] = useState([]);
    const [optAgents, setOptAgents] = useState(initialOptAgents); // Initialize with prop
    const [optLeadSource, setOptLeadSource] = useState(initialOptLeadSource); // Initialize with prop


    // Helper function to update search parameters
    const updateSearchParamsHelper = useCallback((updates, value) => {
        const newSearchParams = new URLSearchParams(searchParams);

        // Handle both single parameter and multiple parameters
        const paramsToUpdate = typeof updates === 'object' && !Array.isArray(updates) && !(updates instanceof Date)
            ? Object.entries(updates)
            : [[updates, value]]; // Fallback to original single param signature

        paramsToUpdate.forEach(([paramName, value]) => {

            if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
                newSearchParams.delete(paramName);
                // If paramName is 'partner', also ensure 'partner[]' is cleared when value is empty
                if (paramName === URL_PARAM_CHANNEL_PARTNERS) {
                    newSearchParams.delete(`${paramName}[]`);
                }
            } else if (Array.isArray(value)) {
                if (viewMode !== "channelPartner" && (paramName === URL_PARAM_LEAD_SOURCES || paramName === URL_PARAM_TAGS)) {
                    newSearchParams.set(paramName, value.map(item => item.label).join(','));
                } else if (paramName === URL_PARAM_CHANNEL_PARTNERS) {
                    // Delete any existing 'partner' or 'partner[]' params
                    newSearchParams.delete(paramName); // Deletes 'partner' if it exists
                    newSearchParams.delete(`${paramName}[]`); // Deletes 'partner[]' if it exists
                    // Append each partner ID as 'partner[]'
                    value.forEach(item => newSearchParams.append(`${paramName}[]`, item.value));
                }
            } else if (typeof value === 'boolean') {
                newSearchParams.set(paramName, value.toString());
            } else if (value instanceof Date && isValid(value)) {
                newSearchParams.set(paramName, format(value, 'yyyy-MM-dd'));
            } else if (typeof value === 'string' || typeof value === 'number') {
                newSearchParams.set(paramName, String(value));
            }
        });
        console.log(newSearchParams.toString())
        setSearchParams(newSearchParams, { replace: true });
    }, [searchParams, setSearchParams, viewMode]);


    // Effect to initialize filter states from URL search parameters
    useEffect(() => {
        const statusParam = searchParams.get(URL_PARAM_STATUS);
        setFilterStatus(statusParam || null);

        const roleParam = searchParams.get(URL_PARAM_ROLE);
        setFilterRole(roleParam || initialRole || null);

        // Initialize filterLeadLabel from URL first, as it influences lead source logic
        let initialFilterLeadLabelFromUrl = null;
        if (viewMode !== "channelPartner") {
            initialFilterLeadLabelFromUrl = searchParams.get(URL_PARAM_LEAD_LABEL) || null;
            console.log(initialFilterLeadLabelFromUrl)
            setFilterLeadLabel(initialFilterLeadLabelFromUrl); // Set state for other effects
            // Also set temporaryLabel if filterLeadLabel is being set from URL
            // This ensures the dropdown shows the correct label if loaded from URL
            if (initialFilterLeadLabelFromUrl) {
                // Attempt to find a matching option if optLeadSource is available, otherwise use the string
                const foundLabelOption = Array.isArray(optLeadSource) && optLeadSource?.find(opt => opt.label === initialFilterLeadLabelFromUrl || opt.value === initialFilterLeadLabelFromUrl);
                setTemporaryLabel(foundLabelOption || initialFilterLeadLabelFromUrl);
            } else {
                setTemporaryLabel(null);
            }
        }


        if (viewMode !== "channelPartner") {
            const leadSourcesParam = searchParams.get(URL_PARAM_LEAD_SOURCES);
            const currentLeadLabelString = initialFilterLeadLabelFromUrl;



            if (currentLeadLabelString !== 'HotLead' && currentLeadLabelString !== 'ColdLead') {
                if (leadSourcesParam) {
                    const urlSourceNames = leadSourcesParam.split(',');
                    if (Array.isArray(optLeadSource) && optLeadSource.length > 0) {
                        const newSelectedOptions = [];
                        const newFilterLeadSourceNames = [];
                        urlSourceNames.forEach(name => {
                            const foundOption = optLeadSource?.find(opt => opt.label === name);
                            if (foundOption) {
                                newSelectedOptions.push(foundOption);
                                newFilterLeadSourceNames.push(foundOption.label);
                            }
                        });
                        setSelectedOptions(newSelectedOptions);
                        setFilterLeadSource(newFilterLeadSourceNames.length > 0 ? newFilterLeadSourceNames : null);

                    } else {
                        setFilterLeadSource(urlSourceNames.length > 0 ? urlSourceNames : null);
                        setSelectedOptions([]);

                    }
                } else {
                    setSelectedOptions([]);
                    setFilterLeadSource(null);

                }
            } else {

            }

            const tagsParam = searchParams.get(URL_PARAM_TAGS);
            if (tagsParam) {
                setSelectedFilterTags(tagsParam.split(',').map(label => ({ value: label, label: label })));
            } else {
                setSelectedFilterTags([]);
            }
        }

        const cpParams = searchParams.getAll(`${URL_PARAM_CHANNEL_PARTNERS}[]`);
        if (cpParams.length > 0) {
            setSelectedChannelPartners(cpParams.map(id => ({ value: id, label: `Partner ${id}` }))); // Placeholder label
        } else {
            // Fallback for single 'partner' param if 'partner[]' is not found (e.g. old URLs or manual entry)
            const singleCpParam = searchParams.get(URL_PARAM_CHANNEL_PARTNERS);
            if (singleCpParam) {
                setSelectedChannelPartners([{ value: singleCpParam, label: `Partner ${singleCpParam}` }]);
            } else {
                setSelectedChannelPartners([]);
            }
        }

        const startDateParam = searchParams.get(URL_PARAM_START_DATE);
        const parsedStartDate = startDateParam ? new Date(startDateParam) : null;
        setRangeStart(isValid(parsedStartDate) ? parsedStartDate : null);

        const endDateParam = searchParams.get(URL_PARAM_END_DATE);
        const parsedEndDate = endDateParam ? new Date(endDateParam) : null;
        setRangeEnd(isValid(parsedEndDate) ? parsedEndDate : null);

        const agentIdParam = searchParams.get(URL_PARAM_AGENT_ID);
        setFilterAgent(agentIdParam || null);
        // selectedAgent update is handled in another useEffect dependent on optAgents

        setNewLeadsSort(searchParams.get(URL_PARAM_NEW_SORT) === 'true');

        const queryParam = searchParams.get(URL_PARAM_SEARCH_QUERY);
        setFilterSearch(queryParam || null);
        setSearch(queryParam || null); // Sync search input

        setFilterLeadStatus(searchParams.get(URL_PARAM_LEAD_STATUS) || null);
        // filterLeadLabel is set earlier in this effect

        const isActive = !!(
            statusParam ||
            roleParam ||
            (viewMode !== "channelPartner" && searchParams.get(URL_PARAM_LEAD_SOURCES)) ||
            (viewMode !== "channelPartner" && searchParams.get(URL_PARAM_TAGS)) ||
            searchParams.has(`${URL_PARAM_CHANNEL_PARTNERS}[]`) || // Check for partner[]
            searchParams.has(URL_PARAM_CHANNEL_PARTNERS) || // Check for partner (single)
            startDateParam ||
            endDateParam ||
            agentIdParam ||
            queryParam ||
            searchParams.get(URL_PARAM_LEAD_STATUS) ||
            (viewMode !== "channelPartner" && searchParams.get(URL_PARAM_LEAD_LABEL)) ||
            searchParams.get(URL_PARAM_NEW_SORT) === 'true'
        );
        setIsFiltering(isActive);
    }, [searchParams, initialRole, optAgents, optLeadSource, viewMode]); // optAgents dependency for selectedAgent logic below, added optLeadSource

    // Update selectedAgent when optAgents loads and agentId is in URL
    useEffect(() => {
        const agentIdParam = searchParams.get(URL_PARAM_AGENT_ID);
        if (agentIdParam && optAgents.length > 0) {
            const agentObj = optAgents.find(a => String(a.value) === String(agentIdParam));
            if (agentObj) {
                setSelectedAgent(agentObj);
            } else {
                setSelectedAgent({ value: agentIdParam, label: `Agent ${agentIdParam}` }); // Fallback
            }
        } else if (!agentIdParam) {
            setSelectedAgent({ value: 'all', label: 'All Agents' });
        }
    }, [searchParams, optAgents]);


    // Fetch all tags for the filter dropdown
    useEffect(() => {
        const fetchAllTags = async () => {
            try {
                const tagsData = await getAllTags(axios);
                if (tagsData) {
                    const formattedTags = tagsData.map((tag) => ({
                        value: tag.name,
                        label: tag.name
                    }));
                    setAllTags(formattedTags);
                }
            } catch (error) {
                console.error('Failed to fetch tags:', error);
                showToast('Failed to load tags for filtering.', 'error');
            }
        };
        if (axios && viewMode !== "channelPartner") fetchAllTags();
    }, [axios, viewMode]);

    // Fetch lead sources (This was in LeadsListTable, moved here)
    useEffect(() => {
        async function fetchLeadSourceData() {
            if (!axios) return;
            try {
                const res = await axios.get(`/leadSources`); // Assuming headers are handled by axios instance
                const result = res?.data?.data;
                let opt = [];
                result?.forEach((source) => // Use forEach if map isn't returning
                    opt.push({
                        value: source.id, // Assuming API returns id and name
                        label: source.name
                    })
                );
                setOptLeadSource(opt);
            } catch (err) {
                console.error('Failed to fetch lead sources:', err);
                showToast('Failed to load lead sources.', 'error');
            }
        }
        if (viewMode !== "channelPartner") fetchLeadSourceData();
    }, [axios, viewMode]);

    // New useEffect to manage filterLeadSource based on filterLeadLabel
    useEffect(() => {
        if (viewMode === "channelPartner" || !axios) return;

        const actualLabelString = typeof filterLeadLabel === 'string'
            ? filterLeadLabel
            : (filterLeadLabel?.value || filterLeadLabel?.label || null);



        const processLeads = async (type) => {
            let leadNames = [];
            try {
                if (type === 'HotLead') {
                    if (memoizedHotLeads) {
                        leadNames = memoizedHotLeads.map(source => source.name);
                    } else {

                        const res = await axios.get(`/leadSources/hotleads`);
                        const fetchedLeads = res?.data?.data || [];
                        setMemoizedHotLeads(fetchedLeads);
                        leadNames = fetchedLeads.map(source => source.name);
                    }
                } else if (type === 'ColdLead') {
                    if (memoizedColdLeads) {
                        leadNames = memoizedColdLeads.map(source => source.name);
                    } else {

                        const res = await axios.get(`/leadSources/coldleads`);
                        const fetchedLeads = res?.data?.data || [];
                        setMemoizedColdLeads(fetchedLeads);
                        leadNames = fetchedLeads.map(source => source.name);
                    }
                }

                setFilterLeadSource(leadNames.length > 0 ? leadNames : null);
                setSelectedOptions([]); // Clear multi-select for lead sources
            } catch (error) {
                console.error(`[Debug LabelEffect] Failed to fetch ${type} sources:`, error);
                showToast(`Failed to load ${type} sources.`, 'error');
                setFilterLeadSource(null); // Clear on error
                setSelectedOptions([]);
            }
        };

        switch (actualLabelString) {
            case 'HotLead':

                processLeads('HotLead');
                break;
            case 'ColdLead':

                processLeads('ColdLead');
                break;
            default:

                break;
        }
    }, [
        filterLeadLabel,
        axios,
        memoizedHotLeads,
        setMemoizedHotLeads,
        memoizedColdLeads,
        setMemoizedColdLeads,
        setFilterLeadSource,
        setSelectedOptions,
        optLeadSource, // Added as per thought process, though current default does no action involving it
        viewMode
    ]);




    // Handler Functions
    const handleStatusOption = useCallback((statusValue) => {
        setCurrentPage(1);
        setFilterStatus(statusValue || null);
        updateSearchParamsHelper(URL_PARAM_STATUS, statusValue || null);
    }, [setCurrentPage, updateSearchParamsHelper]);

    const handleRoleOption = useCallback((roleValue) => {
        setCurrentPage(1);
        setFilterRole(roleValue || null);
        updateSearchParamsHelper(URL_PARAM_ROLE, roleValue || null);
    }, [setCurrentPage, updateSearchParamsHelper]);

    const handleLeadSource = useCallback((selected) => {
        if (viewMode === "channelPartner") return;
        setTemporaryLabel(null); // Or 'Filter by Label'
        setFilterLeadLabel(null);

        setCurrentPage(1);
        setSelectedOptions(selected || []);

        const leadSourceLabels = selected ? selected.map(({ label }) => label) : [];
        setFilterLeadSource(leadSourceLabels.length > 0 ? leadSourceLabels : null);
        updateSearchParamsHelper({ [URL_PARAM_LEAD_SOURCES]: selected || [], [URL_PARAM_LEAD_LABEL]: null, [URL_PARAM_PAGE]: 1 });


    }, [setCurrentPage, updateSearchParamsHelper, viewMode]);

    const handleLeadLabelOption = useCallback((leadLabelValue) => {
        if (viewMode === "channelPartner") return;
        // No direct axios call here anymore, new useEffect will handle it.

        setCurrentPage(1); // Reset pagination early

        let labelStringForLogic = null;
        if (typeof leadLabelValue === 'string') {
            labelStringForLogic = leadLabelValue;
        } else if (leadLabelValue && typeof leadLabelValue === 'object') {
            labelStringForLogic = leadLabelValue.value || leadLabelValue.label || null;
        }


        setTemporaryLabel(leadLabelValue); // For display in dropdown
        setFilterLeadLabel(leadLabelValue); // This will trigger the new useEffect

        const newParams = new URLSearchParams(searchParams);

        if (labelStringForLogic && typeof labelStringForLogic === 'string' && labelStringForLogic.trim() !== '') {
            newParams.set(URL_PARAM_LEAD_LABEL, labelStringForLogic);
            newParams.set(URL_PARAM_PAGE, 1)

        } else {
            newParams.delete(URL_PARAM_LEAD_LABEL);

        }

        // If the label is 'HotLead' or 'ColdLead', or if the label is being cleared,
        // then delete URL_PARAM_LEAD_SOURCES.
        // The new useEffect will populate filterLeadSource for Hot/Cold.
        // If clearing, sources should also clear.
        if (
            labelStringForLogic === 'HotLead' ||
            labelStringForLogic === 'ColdLead' ||
            !labelStringForLogic // Clearing the label
        ) {
            newParams.delete(URL_PARAM_LEAD_SOURCES);

        }
        // Note: If it's a specific non-consolidated label, we DON'T clear URL_PARAM_LEAD_SOURCES here.
        // This allows selecting a specific label AND specific sources simultaneously if desired.
        // The new useEffect for filterLeadLabel also doesn't touch filterLeadSource for specific non-consolidated labels.
        // The useEffect for URL sync will then pick up URL_PARAM_LEAD_SOURCES if it exists.
        setSearchParams(newParams, { replace: true });

        // Removed direct calls to setFilterLeadSource() and setSelectedOptions()
        // These are now handled by the new useEffect triggered by setFilterLeadLabel
        // or by the URL sync useEffect.

    }, [setCurrentPage, searchParams, setSearchParams, viewMode, setTemporaryLabel, setFilterLeadLabel]);

    const handleTagsChange = useCallback((selected) => {
        if (viewMode === "channelPartner") return;
        setSelectedFilterTags(selected || []);
        updateSearchParamsHelper(URL_PARAM_TAGS, selected || []);
        setCurrentPage(1);
    }, [setCurrentPage, updateSearchParamsHelper, viewMode]);

    const handleChannelPartnersChange = useCallback((selectedArray) => {
        setSelectedChannelPartners(selectedArray || []);
        updateSearchParamsHelper(URL_PARAM_CHANNEL_PARTNERS, selectedArray || []);
        setCurrentPage(1);
    }, [setCurrentPage, updateSearchParamsHelper]);

    const handleAgentChange = useCallback((selectedOption) => {
        setCurrentPage(1);
        if (selectedOption && selectedOption.value === 'all') {
            setFilterAgent(null);
            setSelectedAgent({ value: 'all', label: 'All Agents' });
            updateSearchParamsHelper(URL_PARAM_AGENT_ID, null);
        } else if (selectedOption) {
            setFilterAgent(selectedOption.value);
            setSelectedAgent(selectedOption);
            updateSearchParamsHelper(URL_PARAM_AGENT_ID, selectedOption.value);
        } else {
            setFilterAgent(null);
            setSelectedAgent({ value: 'all', label: 'All Agents' });
            updateSearchParamsHelper(URL_PARAM_AGENT_ID, null);
        }
    }, [setCurrentPage, updateSearchParamsHelper]);

    const handleRangeStartChange = useCallback((date) => {
        const validDate = date && isValid(date) ? date : null;
        setRangeStart(validDate);
        updateSearchParamsHelper(URL_PARAM_START_DATE, validDate);
        setCurrentPage(1);
    }, [setCurrentPage, updateSearchParamsHelper]);

    const handleRangeEndChange = useCallback((date) => {
        const validDate = date && isValid(date) ? date : null;
        setRangeEnd(validDate);
        updateSearchParamsHelper(URL_PARAM_END_DATE, validDate);
        setCurrentPage(1);
    }, [setCurrentPage, updateSearchParamsHelper]);

    const handleNewLeadsSort = useCallback(() => {
        const newSortValue = !newLeadsSort;
        setNewLeadsSort(newSortValue);
        updateSearchParamsHelper(URL_PARAM_NEW_SORT, newSortValue);
        setCurrentPage(1);
    }, [newLeadsSort, setCurrentPage, updateSearchParamsHelper]);

    const handleHeaderSearchSubmit = useCallback((term) => {
        const trimmedTerm = term ? term.trim() : null;
        setSearch(trimmedTerm);
        setCurrentPage(1);
        setFilterSearch(trimmedTerm);
        updateSearchParamsHelper(URL_PARAM_SEARCH_QUERY, trimmedTerm);
    }, [setCurrentPage, updateSearchParamsHelper]);

    const handleLeadStatusOption = useCallback((leadStatusValue) => {
        setCurrentPage(1);
        setFilterLeadStatus(leadStatusValue || null);
        updateSearchParamsHelper(URL_PARAM_LEAD_STATUS, leadStatusValue || null);
    }, [setCurrentPage, updateSearchParamsHelper]);

    const clearAllFiltersHandler = useCallback(() => {
        // 1. Reset local filter states to their "cleared" or "default post-clear" values
        setFilterStatus(null); // Or 'Active' if that's a required default post-clear
        setFilterRole(initialRole || null); // Resets to initialRole if provided, else null

        if (viewMode !== "channelPartner") {
            setSelectedOptions([]);
            setFilterLeadSource(null);
            setSelectedFilterTags([]);
            setFilterLeadLabel(null);
            setTemporaryLabel(null);
        }
        setSelectedChannelPartners([]);
        setRangeStart(null);
        setRangeEnd(null);
        setFilterAgent(null);
        setSelectedAgent({ value: 'all', label: 'All Agents' });
        setNewLeadsSort(false); // Assuming 'false' is the cleared state for this sort.
        setSearch(null);
        setFilterSearch(null);
        setFilterLeadStatus(null);

        setCurrentPage(1); // Reset pagination, important for UX.

        // 2. Clear URL parameters
        const newSearchParams = new URLSearchParams();
        // Optional: if specific default parameters are needed in the URL after clearing,
        // set them on newSearchParams here. For example:
        // if (initialRole) { newSearchParams.set(URL_PARAM_ROLE, initialRole); }
        // For a true "clear all filter-related params", an empty newSearchParams is appropriate,
        // unless business logic dictates certain params should persist/appear.
        setSearchParams(newSearchParams, { replace: true });

        // setIsFiltering(false) was removed;
        // The useEffect hook (around line 81) that depends on searchParams will correctly update isFiltering.
    }, [setSearchParams, initialRole, setCurrentPage, viewMode]);

    const handleBadgeRemoveAndLeadSource = useCallback((option) => {
        const updatedOptions = selectedOptions.filter((o) => o.value !== option.value);
        handleLeadSource(updatedOptions); // This updates state and URL params
    }, [selectedOptions, handleLeadSource]);


    // Derived Values
    const clearBadge = useMemo(() => {
        return (
            (rangeStart && isValid(rangeStart)) ||
            (rangeEnd && isValid(rangeEnd)) ||
            filterLeadStatus ||
            (viewMode !== "channelPartner" && selectedOptions && selectedOptions.length > 0) ||
            filterAgent ||
            (viewMode !== "channelPartner" && filterLeadLabel) ||
            (filterRole && filterRole !== initialRole) ||
            (viewMode !== "channelPartner" && selectedFilterTags && selectedFilterTags.length > 0) ||
            (selectedChannelPartners && selectedChannelPartners.length > 0) ||
            filterSearch ||
            (filterStatus && filterStatus !== activeStatus[0]) // Assuming activeStatus[0] is 'Active' and a default
        );
    }, [
        rangeStart, rangeEnd, filterLeadStatus, selectedOptions, filterAgent,
        filterLeadLabel, filterRole, initialRole, selectedFilterTags,
        selectedChannelPartners, filterSearch, filterStatus, viewMode
    ]);

    const baseReturn = {
        filterStatus,
        filterRole,
        selectedChannelPartners,
        rangeStart,
        rangeEnd,
        filterAgent,
        selectedAgent,
        newLeadsSort,
        search,
        filterSearch,
        filterLeadStatus,
        isFiltering,
        optAgents,
        handleStatusOption,
        handleRoleOption,
        handleChannelPartnersChange,
        handleAgentChange,
        handleRangeStartChange,
        handleRangeEndChange,
        handleNewLeadsSort,
        handleHeaderSearchSubmit,
        handleLeadStatusOption,
        clearAllFiltersHandler,
        clearBadge,
    };

    return {
        // All properties from baseReturn
        ...baseReturn,

        // Always include these, their values will be appropriate for the viewMode
        // due to conditional initialization and handler logic within the hook.
        selectedOptions,
        filterLeadSource,
        selectedFilterTags,
        filterLeadLabel,
        temporaryLabel,
        allTags,
        optLeadSource,
        handleLeadSource,
        handleLeadLabelOption,
        handleTagsChange,
        handleBadgeRemoveAndLeadSource,
    };
};

export default useLeadsListFilters;